import { get, post, put, del, downloadFile, postFile, RequestOpts } from "./client";
import { emitTourEvent } from "../lib/tourEvents";
import {
  isPracticeId, practiceCapturesCreates, withPracticeRows, getPracticeVacature,
  updatePracticeVacature, deletePracticeVacature, createPracticeVacature,
} from "../lib/practice";

// ── Response types matching the backend ──────────────────────

export interface DemoStatus {
  demo: boolean;
}

export interface DemoUser {
  name: string;
  email: string;
  role: string;
}

export interface Partner {
  id: number;
  name: string;
  email_domain: string;
  kam_ids: number[];
  logo_url?: string | null;
  /** Square variant of the logo; see lib/partner.ts. */
  icon_url?: string | null;
  created_at: string;
  updated_at?: string | null;
  /** Whether a Google Sheets sync secret is configured — the secret itself is never included here. */
  has_sync_secret: boolean;
}

export interface Branch {
  id: number;
  name: string;
  partner_id: number;
  location: string;
  created_at: string;
}

export interface Vacature {
  id: number;
  functie: string;
  partner_id: number | null;
  branch_id: number | null;
  status: string;         // "open" | "hold" | "gesloten"
  priority: string;       // "hoog" | "normaal" | "laag"
  contactpersoon: string;
  contactpersoon_picture_url: string | null;
  hiring_manager: string;
  department: string | null;
  locatie: string;
  ervaring: string;        // "0 years" | "1-3 years" | "3-5 years" | "5+ years"
  disciplines: string[];
  aantal_posities: number;
  bemiddeling_status: string; // "geen" | "in_bemiddeling" | "shortlisted" | "geplaatst"
  opmerkingen: string;
  salary_type: "range" | "fixed" | "other" | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_fixed: number | null;
  salary_other: string | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
  reviewed_at: string | null;
}

export interface ActivityLog {
  id: number;
  actor_email: string;
  action: string;
  model_name: string;
  record_id: number;
  payload: Record<string, any> | null;
  created_at: string;
}

export interface AuditEntry {
  id: number;
  user_id: number;
  user_name: string;
  action: "create" | "update" | "delete" | "status_change" | "login";
  vacature_id: number | null;
  vacature_functie: string | null;
  partner_name: string | null;
  details: string | null;
  created_at: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: "admin" | "kam" | "hmp" | "partner_admin";
  partner_id: number | null;
  kam_id: number | null;
  secondary_kam_id: number | null;
  branch_ids: number[];
  partner_ids: number[];
  phone: string;
  can_act_as_kam: boolean;
  profile_picture_url: string | null;
}

export interface KamDashboard {
  totaal_vacatures: number;
  open_vacatures: number;
  gesloten_vacatures: number;
  hoge_prioriteit: number;
  in_bemiddeling: number;
  geplaatst: number;
  per_partner: { partner_id: number; partner_name: string; open: number; gesloten: number; hoog: number; in_bemiddeling: number }[];
  recent_activity: ActivityLog[];
}

export interface HmpDashboard {
  open_vacatures: number;
  gesloten_vacatures: number;
  hoge_prioriteit: number;
  in_bemiddeling: number;
  stale_vacatures: {
    id: number;
    functie: string;
    locatie: string;
    days_since_update: number;
    opmerkingen: string;
    status: string;
    priority: string;
    bemiddeling_status: string;
    contactpersoon: string;
    aantal_posities: number;
    salary_type: "range" | "fixed" | "other" | null;
    salary_min: number | null;
    salary_max: number | null;
    salary_fixed: number | null;
    salary_other: string | null;
  }[];
  // Extra review batch offered after stale_vacatures (the primary batch),
  // and the day counts from the HMP's review settings.
  secondary_vacature_ids: number[];
  review_days: number;
  secondary_review_days: number;
}

export interface AuditPage {
  data: ActivityLog[];
  total: number;
  page: number;
  limit: number;
}

export interface PagedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface Notification {
  id: number;
  user_email: string;
  message: string;
  type: string;
  related_vacature_id: number | null;
  related_branch_access_request_id: number | null;
  is_read: boolean;
  created_at: string;
}

// ── Partners ─────────────────────────────────────────────────

export const listPartners  = (opts?: RequestOpts) => get<PagedResponse<Partner>>("/partners", opts);
export const createPartner = (body: { name: string; email_domain: string }, opts?: RequestOpts) =>
  post<Partner>("/partners", body, opts);
export const updatePartner = (id: number, body: Partial<{ name: string; logo_url: string; icon_url: string }>, opts?: RequestOpts) =>
  put<Partner>(`/partners/${id}`, body, opts);
export const deletePartner = (id: number) => del<{ ok: boolean }>(`/partners/${id}`);
export const regeneratePartnerSyncSecret = (id: number) =>
  post<{ sync_secret: string }>(`/partners/${id}/sync-secret`);
export const revokePartnerSyncSecret = (id: number) => del<{ ok: boolean }>(`/partners/${id}/sync-secret`);
export const setPartnerSheetsSyncEnabled = (id: number, enabled: boolean) =>
  put<{ ok: boolean }>(`/partners/${id}/sync-enabled`, { enabled });

// ── Branches ─────────────────────────────────────────────────

export const listBranches = (partnerId?: number) =>
  get<Branch[]>(partnerId ? `/branches?partner_id=${partnerId}` : "/branches");
export const createBranch = (body: { partner_id: number; name: string; location: string }, opts?: RequestOpts) =>
  post<Branch>("/branches", body, opts);
export const updateBranch = (id: number, body: { name: string; location: string }, opts?: RequestOpts) =>
  put<Branch>(`/branches/${id}`, body, opts);
export const deleteBranch = (id: number, opts?: RequestOpts) => del<{ ok: boolean }>(`/branches/${id}`, opts);

export interface BranchAccessRequest {
  id: number;
  user_id: number;
  user_email: string;
  partner_id: number;
  branch_id: number;
  status: "pending" | "fulfilled" | "rejected";
  created_at: string;
  resolved_at: string | null;
}

export interface BranchAccessRequestDetail {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  partner_id: number;
  branch_id: number;
  branch_name: string;
  status: "pending" | "fulfilled" | "rejected";
  created_at: string;
  resolved_at: string | null;
}

export const requestBranchAccess = async (branchId: number) => {
  const res = await post<BranchAccessRequest>("/users/me/branch-requests", { branch_id: branchId });
  emitTourEvent("branch-requested");
  return res;
};

export const listMyBranchRequests = () =>
  get<BranchAccessRequest[]>("/users/me/branch-requests");

export const listUserBranchRequests = (userId: number) =>
  get<BranchAccessRequest[]>(`/users/${userId}/branch-requests`);

export const updateUserBranchPermissions = (userId: number, branchIds: number[], opts?: RequestOpts) =>
  put<User>(`/users/${userId}/branch-permissions`, { branch_ids: branchIds }, opts);

export const updateUserPartnerPermissions = (userId: number, partnerIds: number[], opts?: RequestOpts) =>
  put<User>(`/users/${userId}/partner-permissions`, { partner_ids: partnerIds }, opts);

// A user → department mapping. branch_id null = default for every branch;
// branch-specific mappings replace the defaults for that branch. List order
// is the prefill order (the first suggestion is preselected).
export interface DepartmentMapping {
  branch_id: number | null;
  department: string;
}

export interface DepartmentMappings {
  user_id: number;
  mappings: DepartmentMapping[];
}

export interface DepartmentSuggestion {
  user_id: number | null;
  source: "branch" | "default" | "none";
  departments: string[];
}

export const getUserDepartmentMappings = (userId: number, opts?: RequestOpts) =>
  get<DepartmentMappings>(`/users/${userId}/department-mappings`, opts);

export const updateUserDepartmentMappings = (userId: number, mappings: DepartmentMapping[], opts?: RequestOpts) =>
  put<DepartmentMappings>(`/users/${userId}/department-mappings`, { mappings }, opts);

export const suggestDepartments = (branchId: number, contactpersoon?: string, opts?: RequestOpts) => {
  const q = new URLSearchParams({ branch_id: String(branchId) });
  if (contactpersoon) q.set("contactpersoon", contactpersoon);
  return get<DepartmentSuggestion>(`/users/department-suggestions?${q}`, opts);
};

export const listPendingBranchAccessRequests = (opts?: RequestOpts) =>
  get<BranchAccessRequestDetail[]>("/branch-requests", opts);

export const getBranchAccessRequest = (id: number, opts?: RequestOpts) =>
  get<BranchAccessRequestDetail>(`/branch-requests/${id}`, opts);

export const grantBranchAccessRequest = (id: number, opts?: RequestOpts) =>
  post<BranchAccessRequestDetail>(`/branch-requests/${id}/grant`, undefined, opts);

export const denyBranchAccessRequest = (id: number, opts?: RequestOpts) =>
  post<BranchAccessRequestDetail>(`/branch-requests/${id}/deny`, undefined, opts);

// ── Vacatures ────────────────────────────────────────────────

// Filters on GET /vacatures. status/priority take the frontend values
// ("open" | "hold" | "gesloten", "hoog" | "normaal" | "laag"); q searches
// functie, locatie and contactpersoon. All filtering happens server-side.
export type VacatureFilters = { partner_id?: number; branch_id?: number; bemiddeling_status?: string; status?: string; priority?: string; q?: string };

export const listVacatures = (params: VacatureFilters & { my_hmps?: boolean; mine?: boolean; ids?: number[]; page?: number; limit?: number } = {}) => {
  const q = new URLSearchParams();
  if (params.partner_id)         q.set("partner_id", String(params.partner_id));
  if (params.branch_id)          q.set("branch_id", String(params.branch_id));
  if (params.bemiddeling_status) q.set("bemiddeling_status", params.bemiddeling_status);
  if (params.status)             q.set("status", params.status);
  if (params.priority)           q.set("priority", params.priority);
  if (params.q)                  q.set("q", params.q);
  if (params.my_hmps)            q.set("my_hmps", "true");
  if (params.mine)               q.set("mine", "true");
  if (params.ids && params.ids.length > 0) q.set("ids", params.ids.join(","));
  if (params.page)               q.set("page", String(params.page));
  if (params.limit)              q.set("limit", String(params.limit));
  const qs = q.toString();
  // A help tour's practice vacature (lib/practice.ts) is listed first while one runs.
  return get<PagedResponse<Vacature>>(`/vacatures${qs ? `?${qs}` : ""}`).then(res => withPracticeRows(res, params));
};

// listAllVacatures pages through /vacatures at the backend's max page size
// (200) until every vacature matching the filters is fetched —
// used by "Meerdere aanpassen" to select beyond the currently visible page.
export async function listAllVacatures(params: VacatureFilters): Promise<Vacature[]> {
  const limit = 200;
  const rows: Vacature[] = [];
  for (let page = 1; ; page++) {
    const res = await listVacatures({ ...params, page, limit });
    rows.push(...res.data);
    if (res.data.length < limit || rows.length >= res.total) return rows;
  }
}

// listVacaturesByIds bulk-fetches full vacature detail for a specific set of
// IDs in one request — used to build Swipe Review cards from a grouped
// Tasks entry (e.g. stale_vacature's RelatedVacatureIDs) instead of issuing
// one GET per vacature. limit is set generously above the default page size
// since the caller already knows exactly how many IDs it asked for.
export const listVacaturesByIds = (ids: number[]) =>
  listVacatures({ ids, limit: Math.max(ids.length, 1) });

// Practice vacatures (negative IDs, lib/practice.ts) never reach the backend.
export const getVacature = async (id: number) =>
  isPracticeId(id) ? getPracticeVacature(id) : get<Vacature>(`/vacatures/${id}`);
export const getVacatureActivity = async (id: number) =>
  isPracticeId(id) ? [] : get<ActivityLog[]>(`/vacatures/${id}/activity`);

export const exportVacatures = (partnerId: number, opts?: RequestOpts) => {
  const qs = partnerId ? `?partner_id=${partnerId}` : "";
  const today = new Date().toISOString().slice(0, 10);
  return downloadFile(`/vacatures/export${qs}`, `vacatures-export-${today}.xlsx`, opts).then(res => {
    emitTourEvent("export");
    return res;
  });
};

type CreateVacatureBody = {
  functie: string;
  partner_id: number;
  branch_id: number;
  priority?: string;
  contactpersoon?: string;
  hiring_manager?: string;
  department?: string | null;
  locatie?: string;
  ervaring?: string;
  disciplines?: string[];
  aantal_posities?: number;
  opmerkingen?: string;
  salary_type?: "" | "range" | "fixed" | "other";
  salary_min?: number | null;
  salary_max?: number | null;
  salary_fixed?: number | null;
  salary_other?: string | null;
};

export const createVacature = async (body: CreateVacatureBody, opts?: RequestOpts) => {
  const res = practiceCapturesCreates()
    ? createPracticeVacature({ ...body, salary_type: body.salary_type || null } as Partial<Vacature>, body.contactpersoon ?? "")
    : await post<Vacature>("/vacatures", body, opts);
  emitTourEvent("vacature-created");
  return res;
};

type UpdateVacatureBody = {
  functie?: string;
  status?: string;
  priority?: string;
  contactpersoon?: string;
  hiring_manager?: string;
  department?: string | null;
  locatie?: string;
  ervaring?: string;
  disciplines?: string[];
  aantal_posities?: number;
  bemiddeling_status?: string;
  opmerkingen?: string;
  branch_id?: number;
  reviewed_at?: string;
  salary_type?: "" | "range" | "fixed" | "other";
  salary_min?: number | null;
  salary_max?: number | null;
  salary_fixed?: number | null;
  salary_other?: string | null;
};

export const updateVacature = async (id: number, body: UpdateVacatureBody, opts?: RequestOpts) => {
  const res = isPracticeId(id)
    ? updatePracticeVacature(id, { ...body, salary_type: body.salary_type || null } as Partial<Vacature>)
    : await put<Vacature>(`/vacatures/${id}`, body, opts);
  emitTourEvent("vacature-updated");
  return res;
};

export const deleteVacature = async (id: number, opts?: RequestOpts) => {
  if (isPracticeId(id)) deletePracticeVacature(id);
  const res = isPracticeId(id) ? { ok: true } : await del<{ ok: boolean }>(`/vacatures/${id}`, opts);
  emitTourEvent("vacature-deleted");
  return res;
};

// ── Bullhorn ──────────────────────────────────────────────────

export interface BullhornAddress {
  address1: string;
  city: string;
  state: string;
  zip: string;
}

export interface BullhornJobOrder {
  id: number;
  title: string;
  clientCorporation: { id: number; name: string };
  correlatedCustomText10: string | null;
  address: BullhornAddress;
}

export interface BullhornSearchResult {
  data: BullhornJobOrder[];
  total: number;
  count: number;
  start: number;
}

export const bullhornSearchJobOrders = (query: string, opts?: RequestOpts) =>
  get<BullhornSearchResult>(`/bullhorn/search/JobOrder?query=${encodeURIComponent(query)}&fields=id,clientCorporation,correlatedCustomText10,address,title&count=25`, opts);

// ── Dashboard ────────────────────────────────────────────────

export const getKamDashboard  = () => get<KamDashboard>("/dashboard/kam");
export const getAdminDashboard = () => get<KamDashboard>("/dashboard/admin");
export const getHmpDashboard  = (partnerId: number) => get<HmpDashboard>(`/dashboard/hmp/${partnerId}`);

// ── Users ────────────────────────────────────────────────────

export const listUsers   = () => get<PagedResponse<User>>("/users");
export const createUser  = (body: { name: string; email: string; role: string; partner_id?: number; kam_id?: number; secondary_kam_id?: number | null; send_invite?: boolean; can_act_as_kam?: boolean }, opts?: RequestOpts) =>
  post<User>("/users", body, opts);
export const updateUser  = (id: number, body: Partial<User>, opts?: RequestOpts) => put<User>(`/users/${id}`, body, opts);
export const deleteUser  = (id: number, opts?: RequestOpts) => del<{ ok: boolean }>(`/users/${id}`, opts);
export const uploadUserPicture = (id: number, file: File, opts?: RequestOpts) => postFile<User>(`/users/${id}/picture`, "file", file, opts);
export const deleteUserPicture = (id: number, opts?: RequestOpts) => del<User>(`/users/${id}/picture`, opts);
export const listKams    = () => get<PagedResponse<User>>("/users/kams");
export const listOrgContacts = (partnerId?: number) =>
  get<PagedResponse<User>>(`/users/org-contacts?limit=200${partnerId != null ? `&partner_id=${partnerId}` : ""}`);
export const sendInvite  = (userId: number) => post<{ ok: boolean }>(`/admin/mail/send/${userId}`);

// ── Profile ──────────────────────────────────────────────────

export interface WavesContact {
  name: string;
  email: string;
  phone: string;
  profile_picture_url: string | null;
}

export interface Profile {
  id: number;
  name: string;
  email: string;
  role: "admin" | "kam" | "hmp" | "partner_admin";
  phone: string;
  profile_picture_url: string | null;
  waves_contact: WavesContact | null; // waves_contacts[0]
  waves_contacts: WavesContact[];
  /** IDs of the help tours (lib/tours.ts) this user finished. */
  completed_tours: string[];
}

export const getProfile = () => get<Profile>("/profile");
export const updateProfilePhone = (phone: string, opts?: RequestOpts) => put<Profile>("/profile", { phone }, opts);
export const uploadProfilePicture = (file: File, opts?: RequestOpts) => postFile<Profile>("/profile/picture", "file", file, opts);
export const deleteProfilePicture = (opts?: RequestOpts) => del<Profile>("/profile/picture", opts);
// HMP only: replaces the full list of finished help tours (empty resets them all).
export const updateCompletedTours = (completedTours: string[], opts?: RequestOpts) =>
  put<Profile>("/profile/tours", { completed_tours: completedTours }, opts);

// ── Vacature review settings (HMP only) ──────────────────────
// Statuses/priorities use the same values as Vacature ("open"/"hold",
// "hoog"/"normaal"/"laag"). "gesloten" is never part of a review.

export type ReviewStatus = "open" | "hold";
export type ReviewPriority = "hoog" | "normaal" | "laag";

export interface ReviewRule {
  days: number;
  statuses: ReviewStatus[];
  priorities: ReviewPriority[];
}

export interface ReviewSettings {
  primary: ReviewRule;
  secondary: ReviewRule & { enabled: boolean };
}

export const getOwnReviewSettings = () => get<ReviewSettings>("/profile/review-settings");
export const updateOwnReviewSettings = (body: ReviewSettings, opts?: RequestOpts) =>
  put<ReviewSettings>("/profile/review-settings", body, opts);
export const getUserReviewSettings = (userId: number) => get<ReviewSettings>(`/users/${userId}/review-settings`);
export const updateUserReviewSettings = (userId: number, body: ReviewSettings, opts?: RequestOpts) =>
  put<ReviewSettings>(`/users/${userId}/review-settings`, body, opts);

// ── Audit ────────────────────────────────────────────────────

export const listAudit = (page = 1, limit = 25, filters?: { actorEmail?: string; actorDomain?: string; partnerId?: number }) => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters?.actorEmail)      params.set("actor_email", filters.actorEmail);
  if (filters?.actorDomain)     params.set("actor_domain", filters.actorDomain);
  if (filters?.partnerId != null) params.set("partner_id", String(filters.partnerId));
  return get<AuditPage>(`/audit?${params.toString()}`);
};

// ── Auth / Demo ──────────────────────────────────────────────

export const getDemoStatus = () => get<DemoStatus>("/auth/demo-status");

// ── Notifications ────────────────────────────────────────────

export const listNotifications  = (page = 1, limit = 200) =>
  get<PagedResponse<Notification>>(`/notifications?page=${page}&limit=${limit}`);
export const getUnreadCount     = () => get<{ count: number }>("/notifications/unread-count");
export const markAllRead        = () => put<{ ok: boolean }>("/notifications/read-all");
export const markNotificationRead = (id: number) => put<{ ok: boolean }>(`/notifications/${id}/read`);

// ── Chat ─────────────────────────────────────────────────────

export interface ChatMessage {
  id: number;
  chat_id: number;
  sender_id: number;
  sender_name: string;
  body: string;
  created_at: string;
  // Preview of the message this one responds to; null for a plain message
  // (and always null on a chat's last_message).
  reply_to?: ChatMessageReply | null;
}

export interface ChatMessageReply {
  id: number;
  sender_id: number;
  sender_name: string;
  body: string;
}

export interface Chat {
  id: number;
  partner_id: number;
  partner_name: string;
  partner_logo_url: string | null;
  partner_icon_url: string | null;
  job_opening_id: number | null;
  // Algemeen chats are one-on-one: the partner user (hmp/partner_admin) on
  // the partner side. Null for vacature chats.
  user_id: number | null;
  user_name: string | null;
  // Read-only Algemeen chat without a partner user any more (legacy
  // partner-wide chat, or its user was deleted/moved) — admin/kam only.
  archived: boolean;
  vacature_title: string | null;
  vacature_location: string | null;
  updated_at: string;
  last_message: ChatMessage | null;
  unread_count: number;
}

export interface ChatListResponse {
  chats: Chat[];
}

export interface ChatMessagesResponse {
  messages: ChatMessage[];
  has_more: boolean;
}

// user_id is the partner user of an Algemeen chat — required for admin/kam,
// omitted by partner users (always themselves) and for vacature chats.
export const resolveChat = async (params: { partner_id?: number; job_opening_id?: number; user_id?: number }) => {
  if (params.job_opening_id != null && isPracticeId(params.job_opening_id)) throw new Error("Een oefenvacature heeft geen chat.");
  return post<Chat>("/chats/resolve", params);
};

export const listChats = () => get<ChatListResponse>("/chats");

export const listChatMessages = (chatId: number, before?: number, limit = 50) => {
  const q = new URLSearchParams();
  if (before) q.set("before", String(before));
  q.set("limit", String(limit));
  return get<ChatMessagesResponse>(`/chats/${chatId}/messages?${q.toString()}`);
};

// replyToId must be a message in the same chat.
export const sendChatMessage = async (chatId: number, body: string, replyToId?: number) => {
  const res = await post<ChatMessage>(`/chats/${chatId}/messages`, { body, reply_to_id: replyToId ?? null });
  emitTourEvent("chat-sent");
  return res;
};

export const markChatRead = (chatId: number) =>
  post<{ ok: boolean }>(`/chats/${chatId}/read`);

export const getUnreadChatCount = () => get<{ count: number }>("/chats/unread-count");

// ── Tasks ────────────────────────────────────────────────────

export type TaskType =
  | "stale_vacature"
  | "email_approval"
  | "branch_access_request"
  | "kam_no_partner_permissions"
  | "partner_no_users"
  | "hmp_no_branch_permissions"
  | "hmp_no_kam";

export interface Task {
  type: TaskType;
  title: string;
  message: string;
  created_at: string;
  // Every task type is grouped server-side (at most one Task per type).
  // Click-throughs navigate to a fixed page regardless of which item is
  // included, so most types carry no per-item IDs. The two exceptions:
  // related_vacature_ids (stale_vacature) is used to bulk-fetch Swipe
  // Review card data; related_user_ids (hmp_no_branch_permissions) isn't
  // used by the frontend at all — it only exists for backend correctness
  // tests (see backend/internal/dto/task.go).
  related_vacature_ids?: number[] | null;
  related_user_ids?: number[] | null;
  // stale_vacature only: the extra batch offered at the end of the review.
  secondary_vacature_ids?: number[] | null;
}

export interface TaskListResponse {
  tasks: Task[];
  total: number;
}

export const listTasks = () => get<TaskListResponse>("/tasks");

// ── Partner Admin dashboard ───────────────────────────────────

export interface HMPActivity {
  id: number;
  name: string;
  email: string;
  last_login: string | null;
  days_since_login: number | null;
  vacatures_added_30d: number;
  vacatures_updated_30d: number;
  stale_vacatures: number;
  total_vacatures: number;
}

export interface PerVestiging {
  branch_name: string;
  open: number;
  hoog: number;
  in_bemiddeling: number;
}

export interface PartnerAdminDashboardData {
  partner_name: string;
  totaal_vacatures: number;
  open_vacatures: number;
  hoge_prioriteit: number;
  in_bemiddeling: number;
  geplaatst: number;
  hmp_activity: HMPActivity[];
  per_vestiging: PerVestiging[];
  recent_activity: ActivityLog[];
}

export const getPartnerAdminDashboard = (opts?: RequestOpts) =>
  get<PartnerAdminDashboardData>("/dashboard/partner-admin", opts);

// ── KAM vacatures (dashboard drill-down) ─────────────────────

export interface KamVacature {
  id: number;
  functie: string;
  partner_name?: string;
  locatie?: string | null;
  status: string;
  priority: string;
  bemiddeling_status: string;
  updated_at: string;
}

export const listKamVacatures = (
  filterParams: string,
  myHmps: boolean,
  kamId?: number,
  opts?: RequestOpts,
) => {
  const params = new URLSearchParams(filterParams);
  if (myHmps) params.set("my_hmps", "true");
  if (kamId)  params.set("kam_id", String(kamId));
  return get<PagedResponse<KamVacature>>(`/dashboard/kam/vacatures?${params.toString()}`, opts);
};

// ── Function Title Suggestions ───────────────────────────────

export interface FunctionTitleSuggestion {
  id: number;
  title: string;
  partner_id: number | null;
  priority: string | null;       // "hoog" | "normaal" | "laag" | null
  experience: string | null;
  disciplines: string[] | null;
  locatie: string | null;
  aantal_posities: number | null;
  opmerkingen: string | null;
  salary_type: "range" | "fixed" | "other" | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_fixed: number | null;
  salary_other: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface FunctionTitleSuggestionInput {
  title: string;
  partner_id?: number | null;
  priority?: string | null;
  experience?: string | null;
  disciplines?: string[] | null;
  locatie?: string | null;
  aantal_posities?: number | null;
  opmerkingen?: string | null;
  salary_type?: "range" | "fixed" | "other" | null;
  salary_min?: number | null;
  salary_max?: number | null;
  salary_fixed?: number | null;
  salary_other?: string | null;
}

export const listFunctionTitleSuggestions   = () => get<FunctionTitleSuggestion[]>("/function-title-suggestions");
export const createFunctionTitleSuggestion  = (body: FunctionTitleSuggestionInput) => post<FunctionTitleSuggestion>("/function-title-suggestions", body);
export const updateFunctionTitleSuggestion  = (id: number, body: FunctionTitleSuggestionInput) => put<FunctionTitleSuggestion>(`/function-title-suggestions/${id}`, body);
export const deleteFunctionTitleSuggestion  = (id: number) => del<{ ok: boolean }>(`/function-title-suggestions/${id}`);
export const listApplicableFunctionTitleSuggestions = (partnerId?: number) =>
  get<FunctionTitleSuggestion[]>(`/function-title-suggestions/applicable${partnerId ? `?partner_id=${partnerId}` : ""}`);

// ── Alert Subscriptions ───────────────────────────────────────

export type RuleType = "new_vacancy" | "vacancy_update" | "stale_vacancy" | "approval_reminder";
export type AlertFrequency = "immediate" | "hourly" | "daily" | "weekly" | "monthly";

export interface SubscriptionConfig {
  branch_ids?: number[];
  // Deprecated vestiging filter (matched on vacature location); ignored by the
  // backend, only still present on subscriptions saved before branch_ids.
  locations?: string[];
  stale_days?: number;
  own_only?: boolean;
  partner_ids?: number[];
}

export interface UserAlertSubscription {
  id: number;
  user_id: number;
  rule_type: RuleType;
  enabled: boolean;
  frequency: AlertFrequency;
  config: SubscriptionConfig;
  send_email: boolean;
  // Daily/weekly digest send slot, Europe/Amsterdam time. send_weekday is
  // ISO (1 = maandag … 7 = zondag) and only used for weekly.
  send_hour: number;
  send_minute: number;
  send_weekday: number;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionInput {
  enabled: boolean;
  frequency: AlertFrequency;
  config: SubscriptionConfig;
  send_email: boolean;
  // Omitted fields keep their stored value. send_minute must be 0/15/30/45.
  send_hour?: number;
  send_minute?: number;
  send_weekday?: number;
}

export const listSubscriptions     = () => get<UserAlertSubscription[]>("/subscriptions");
export const upsertSubscription    = (ruleType: RuleType, body: SubscriptionInput) =>
  put<UserAlertSubscription>(`/subscriptions/${ruleType}`, body);
export const deleteSubscription    = (ruleType: RuleType) =>
  del<{ ok: boolean }>(`/subscriptions/${ruleType}`);

export const listUserSubscriptions = (userId: number) =>
  get<UserAlertSubscription[]>(`/users/${userId}/subscriptions`);
export const adminUpsertSubscription = (userId: number, ruleType: RuleType, body: SubscriptionInput) =>
  put<UserAlertSubscription>(`/users/${userId}/subscriptions/${ruleType}`, body);
export const adminDeleteSubscription = (userId: number, ruleType: RuleType) =>
  del<{ ok: boolean }>(`/users/${userId}/subscriptions/${ruleType}`);

// ── System status (admin) ─────────────────────────────────────

export interface IntegrationStatus {
  status: "ok" | "error" | "unconfigured" | "disabled";
  live: boolean;
  error?: string;
}

export interface SystemStatus {
  backend_version: string;
  migration: {
    version: number;
    dirty: boolean;
    available: boolean;
  };
  sheet_sync: {
    last_push: string | null;
    last_pull: string | null;
  };
  integrations: {
    bullhorn: IntegrationStatus;
    resend: IntegrationStatus;
    teams: IntegrationStatus;
  };
  email_counts: {
    last_1_day: number;
    last_7_days: number;
    last_30_days: number;
  };
}

export const getSystemStatus = (opts?: RequestOpts) => get<SystemStatus>("/admin/system-status", opts);

// ── Email jobs (admin) ─────────────────────────────────────────

export interface EmailJob {
  id: number;
  recipient_email: string;
  recipient_name: string;
  email_type: string;
  scheduled_for: string;
  sent_at: string | null;
  attempts: number;
  last_error: string;
  created_at: string;
  status: "sent" | "failed" | "pending";
  partner_id: number | null;
  approval_status: "approved" | "pending" | "capped" | "rejected";
  approved_by_email: string | null;
  approved_at: string | null;
  rejected_by_email: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
}

export interface EmailJobPage {
  data: EmailJob[];
  total: number;
  page: number;
  limit: number;
}

export const listEmailJobs = (
  page = 1,
  limit = 25,
  filters?: {
    recipientEmail?: string;
    emailType?: string;
    status?: string;
    approvalStatus?: string;
    createdFrom?: string;
    createdTo?: string;
  }
) => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters?.recipientEmail) params.set("recipient_email", filters.recipientEmail);
  if (filters?.emailType)      params.set("email_type", filters.emailType);
  if (filters?.status)         params.set("status", filters.status);
  if (filters?.approvalStatus) params.set("approval_status", filters.approvalStatus);
  if (filters?.createdFrom)    params.set("created_from", filters.createdFrom);
  if (filters?.createdTo)      params.set("created_to", filters.createdTo);
  return get<EmailJobPage>(`/admin/emails?${params.toString()}`);
};

export const approveEmailJob = (id: number) => post<{ ok: boolean }>(`/admin/emails/${id}/approve`, {});
export const rejectEmailJob  = (id: number, reason?: string) =>
  post<{ ok: boolean }>(`/admin/emails/${id}/reject`, reason ? { reason } : {});

export interface BulkEmailJobActionResult {
  affected: number;
}
export const bulkApproveEmailJobs = (ids: number[]) =>
  post<BulkEmailJobActionResult>("/admin/emails/bulk-approve", { ids });
export const bulkRejectEmailJobs = (ids: number[], reason?: string) =>
  post<BulkEmailJobActionResult>("/admin/emails/bulk-reject", reason ? { ids, reason } : { ids });

export interface EmailJobPreview {
  subject: string;
  html: string;
}
export const previewEmailJob = (id: number, opts?: RequestOpts) => get<EmailJobPreview>(`/admin/emails/${id}/preview`, opts);

// ── Google Sheets sync run log (admin) ─────────────────────────

export type SyncRunStatus = "running" | "success" | "warning" | "error";
export type SyncRunTrigger = "scheduled_pull" | "edit_push" | "manual" | "";

export interface SyncPushLog {
  inserted: { id: number; tab_name: string; row_index: number; sheet_row?: number; reinserted_from?: number | null }[];
  updated: {
    id: number;
    tab_name: string;
    fields: string[];
    /** Old → new value per changed field, as the sheet shows them. Missing in older logs. */
    changes?: { field: string; old: string; new: string }[] | null;
  }[];
  unchanged: number;
  skipped: {
    id: number | null;
    tab_name: string;
    row_index: number;
    /** 1-based row within the tab; missing in logs from older scripts. */
    sheet_row?: number;
    reason: string;
    /** The branch the job opening really belongs to, for a "tab name does not match a branch" skip. */
    branch_name?: string;
  }[];
  errors: { id: number | null; tab_name: string; row_index: number; sheet_row?: number; error: string }[];
  /** The partner's branch names — the tab names that sync. Missing in older logs. */
  branches?: string[];
  error?: string;
  rolled_back: boolean;
}

export interface SyncDeleteLog {
  deleted: number[];
  skipped: number[];
  error?: string;
  rolled_back: boolean;
}

export interface SyncSheetResult {
  pulled: number;
  updated_ids: number[] | null;
  appended_ids: number[] | null;
  marked_deleted_ids: number[] | null;
  stale_cleared_ids: number[] | null;
  held_deletion_ids: number[] | null;
  pushed: number;
  skipped_rows: {
    tab_name: string;
    row: number;
    reason: string;
    id?: number;
    /** For a duplicate-ID skip: where the first row with that ID is. */
    first_tab?: string;
    first_row?: number;
  }[] | null;
  /** Google Sheets URL and tab name → sheet ID, for links to a tab and row. */
  spreadsheet_url?: string;
  tab_gids?: Record<string, number>;
}

export interface SyncSheetsLog {
  id: number;
  run_id: string;
  partner_id: number;
  trigger: SyncRunTrigger;
  status: SyncRunStatus;
  started_at: string | null;
  finished_at: string | null;
  inserted_count: number;
  updated_count: number;
  deleted_count: number;
  skipped_count: number;
  sheet_changed_count: number;
  error_count: number;
  push_result: SyncPushLog | null;
  delete_result: SyncDeleteLog | null;
  sheet_result: SyncSheetResult | null;
  errors: string[] | null;
  warnings: string[] | null;
  created_at: string;
  updated_at: string;
}

/** A job opening a sync log refers to, so IDs can be shown with their title. */
export interface SyncVacatureRef {
  id: number;
  role: string;
  branch_name?: string;
  /** Deleted: the role comes from the activity log's delete snapshot. */
  deleted: boolean;
}

export interface SyncSheetsLogPage {
  data: SyncSheetsLog[];
  total: number;
  page: number;
  limit: number;
  /** Every job opening the page's logs refer to, keyed by ID. */
  vacatures: Record<string, SyncVacatureRef>;
}

export interface SyncSheetsLogFilters {
  partnerId?: number;
  status?: SyncRunStatus;
  trigger?: Exclude<SyncRunTrigger, "">;
  /** Only runs that mention this job opening. */
  vacatureId?: number;
  /** RFC3339 timestamps. */
  createdFrom?: string;
  createdTo?: string;
}

export const listSyncSheetsLogs = (page = 1, limit = 25, filters?: SyncSheetsLogFilters) => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters?.partnerId)   params.set("partner_id", String(filters.partnerId));
  if (filters?.status)      params.set("status", filters.status);
  if (filters?.trigger)     params.set("trigger", filters.trigger);
  if (filters?.vacatureId)  params.set("vacature_id", String(filters.vacatureId));
  if (filters?.createdFrom) params.set("created_from", filters.createdFrom);
  if (filters?.createdTo)   params.set("created_to", filters.createdTo);
  return get<SyncSheetsLogPage>(`/admin/sync-logs?${params.toString()}`);
};

/** One partner's Google Sheets sync health. */
export interface SyncPartnerOverview {
  partner_id: number;
  partner_name: string;
  last_pull_at: string | null;
  last_push_at: string | null;
  last_run_status: SyncRunStatus | "";
  last_run_at: string | null;
  /** Switched off by an admin: the backend refuses every sync request and the script skips its runs. */
  sync_enabled: boolean;
  sync_disabled_at: string | null;
  /** Sync on, but no successful pull for 15+ minutes, or never. */
  stale: boolean;
  runs_24h: number;
  errors_24h: number;
  warnings_24h: number;
  held_deletion_ids: number[];
  held_since: string | null;
}

export const getSyncOverview = (opts?: RequestOpts) =>
  get<SyncPartnerOverview[]>("/admin/sync-logs/overview", opts);

/** A problem that recurs across logged sync runs. */
export interface SyncIssue {
  partner_id: number;
  kind: "error" | "warning" | "skipped";
  message: string;
  tab_name?: string;
  row?: number;
  id?: number;
  /** Logged runs it appeared in. */
  count: number;
  first_seen: string;
  last_seen: string;
  last_run_id: number;
}

export const listSyncIssues = (partnerId?: number, days = 7) => {
  const params = new URLSearchParams({ days: String(days) });
  if (partnerId) params.set("partner_id", String(partnerId));
  return get<SyncIssue[]>(`/admin/sync-logs/issues?${params.toString()}`);
};
